import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

// Helper function for formatting currency with expanded support
const formatCurrency = (amount, currency = 'USD') => {
  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'CHF',
    CNY: '¥',
    INR: '₹',
    BRL: 'R$',
    MXN: '$',
    ZAR: 'R',
    AED: 'د.إ',
    SAR: 'ر.س',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    THB: '฿',
    IDR: 'Rp',
    MYR: 'RM',
    PHP: '₱',
    SGD: 'S$',
    KRW: '₩',
    TRY: '₺',
    RUB: '₽',
    HKD: 'HK$',
    NZD: 'NZ$',
    EGP: 'E£',
    PKR: '₨',
    VND: '₫',
    COP: '$',
    ARS: '$',
    CLP: '$',
    PEN: 'S/',
    UAH: '₴',
    KES: 'KSh',
    NGN: '₦',
    GHS: '₵',
    MAD: 'د.م.',
    TND: 'د.ت',
    ETB: 'Br',
    BDT: '৳',
    LKR: 'Rs',
    MMK: 'K',
    HUF: 'Ft',
    CZK: 'Kč',
    ILS: '₪',
    JOD: 'د.ا',
    KWD: 'د.ك',
    QAR: 'ر.ق',
    OMR: 'ر.ع.',
    BHD: 'د.ب',
    default: ''
  };

  const symbol = currencySymbols[currency] || currencySymbols.default;
  const formattedAmount = amount?.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }) || '0';

  const symbolAfter = ['SEK', 'NOK', 'DKK', 'CZK', 'HUF', 'PLN'];
  
  if (symbolAfter.includes(currency)) {
    return `${formattedAmount} ${symbol}`;
  }

  return currency && !currencySymbols[currency] 
    ? `${formattedAmount} ${currency}`
    : `${symbol}${formattedAmount}`;
};

// Helper function for insight severity colors
const getInsightSeverityColor = (severity) => {
  const colors = {
    critical: { background: 'bg-red-500/10', border: 'border border-red-500/30', icon: '🚨' },
    warning: { background: 'bg-orange-500/10', border: 'border border-orange-500/30', icon: '⚠️' },
    info: { background: 'bg-blue-500/10', border: 'border border-blue-500/30', icon: 'ℹ️' },
    success: { background: 'bg-green-500/10', border: 'border border-green-500/30', icon: '✅' },
  };
  return colors[severity.toLowerCase()] || colors.info;
};

// Get objective display name
const getObjectiveDisplayName = (objective) => {
  const objectiveMap = {
    'OUTCOME_TRAFFIC': 'Traffic',
    'OUTCOME_AWARENESS': 'Awareness',
    'OUTCOME_ENGAGEMENT': 'Engagement',
    'OUTCOME_LEADS': 'Lead Gen',
    'OUTCOME_SALES': 'Conversions',
    'OUTCOME_APP_PROMOTION': 'App Promotion',
    'POST_ENGAGEMENT': 'Engagement',
    'LINK_CLICKS': 'Traffic',
    'REACH': 'Awareness',
    'BRAND_AWARENESS': 'Awareness',
    'LEAD_GENERATION': 'Lead Gen',
    'CONVERSIONS': 'Conversions',
    'APP_INSTALLS': 'App Promotion',
    'VIDEO_VIEWS': 'Video Views',
    'MESSAGES': 'Messages',
    'messages': 'Messages'
  };
  
  return objectiveMap[objective] || objective?.toLowerCase().replace(/_/g, ' ') || 'conversions';
};

const CampaignDetective = () => {
  const navigate = useNavigate();
  const accountDropdownRef = useRef(null);
  const settingsDropdownRef = useRef(null);
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();

  // View Mode State
  const [viewMode, setViewMode] = useState('our_metrics');
  const [hasCustomBenchmarks, setHasCustomBenchmarks] = useState(false);
  const [benchmarkCoverage, setBenchmarkCoverage] = useState(null);
  const [switchingView, setSwitchingView] = useState(false);

  // State declarations
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [geminiAnalysis, setGeminiAnalysis] = useState(null);
  const [healthyCampaignsCount, setHealthyCampaignsCount] = useState(0);
  const [championsCount, setChampionsCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [aiInsights, setAiInsights] = useState([]);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showManageDataModal, setShowManageDataModal] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [campaignsPerPage, setCampaignsPerPage] = useState(50);
  const [currentStatusIndex, setCurrentStatusIndex] = useState(-1);
  const [showingAlert, setShowingAlert] = useState(false);
  const [showPagesModal, setShowPagesModal] = useState(false);
  const [facebookPages, setFacebookPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [pagesConnected, setPagesConnected] = useState(false);
  const [selectedPages, setSelectedPages] = useState([]);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [mainCurrency, setMainCurrency] = useState('USD');
  const [refreshing, setRefreshing] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://whydiditdie.com';

  // Check if user has custom benchmarks
  const checkBenchmarkCoverage = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${BACKEND_URL}/api/benchmarks/coverage`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setBenchmarkCoverage(data);
        setHasCustomBenchmarks(data.ready_for_custom_snapshot);
      }
    } catch (error) {
      logger.error('Error checking benchmark coverage:', error);
    }
  }, [BACKEND_URL]);

 // Fetch campaigns based on view mode
const fetchCampaignsByMode = useCallback(async (mode) => {
  try {
    setLoading(true);
    setError(null);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("Not authenticated");
    }

    let snapshotData;
    let snapshotError;

    if (mode === 'custom_benchmarks') {
      // 🎯 Fetch CUSTOM snapshot from campaign_detective_custom_snapshot table
      logger.info('🎯 Fetching from campaign_detective_custom_snapshot table...');
      const { data, error } = await supabase
        .from('campaign_detective_custom_snapshot')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'ACTIVE')
        .order('performance_score', { ascending: false });

      snapshotData = data;
      snapshotError = error;

      logger.info(`🎯 Custom snapshot results: ${snapshotData?.length || 0} campaigns`);

      if (snapshotError) {
        logger.error('❌ Custom snapshot error:', snapshotError);
        throw new Error(`Failed to fetch custom snapshot: ${snapshotError.message}`);
      }
      
      if (!snapshotData || snapshotData.length === 0) {
        setAiInsights([{
          type: 'info',
          severity: 'info',
          title: 'No Custom Benchmarks Set',
          message: 'You haven\'t set custom benchmarks for any campaigns yet.',
          action: 'Go to Performance Goals to set your custom benchmarks.',
        }]);
      }
    } else {
      // ✅ Fetch PLATFORM snapshots from campaign_detective_snapshot table
      logger.info('📊 Fetching from campaign_detective_snapshot table...');
      const { data, error } = await supabase
        .from('campaign_detective_snapshot')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'ACTIVE')
        .order('spend', { ascending: false });

      snapshotData = data;
      snapshotError = error;

      logger.info(`📊 Platform snapshot results: ${snapshotData?.length || 0} campaigns`);

      if (snapshotError) {
        logger.error('❌ Platform snapshot error:', snapshotError);
        throw new Error(`Failed to fetch snapshot: ${snapshotError.message}`);
      }
    }

    if (!snapshotData || snapshotData.length === 0) {
      setCampaigns([]);
      setFilteredCampaigns([]);
      setHealthyCampaignsCount(0);
      setChampionsCount(0);
      setCriticalCount(0);
      
      if (mode === 'our_metrics') {
        setAiInsights([{
          type: 'info',
          severity: 'info',
          title: 'No Active Campaigns',
          message: 'No active campaign data found. Your campaigns may be paused or you need to sync your data.',
          action: 'Click "Refresh Data" to sync from Facebook.',
        }]);
      }
      
      setDataLoaded(true);
      setLoading(false);
      return;
    }

    // Map snapshot data to display format
    const campaignsToDisplay = snapshotData.map(s => ({
      ...s,
      campaign_id: s.campaign_id,
      name: s.name,
      actual_status: s.effective_status?.toLowerCase() || s.status?.toLowerCase() || 'unknown',
      campaign_objective: s.campaign_objective,
      wasteAmount: s.waste_amount,
      wastePercentage: s.waste_percentage,
      id: s.campaign_id,
      searchableId: s.campaign_id,
      displayId: s.name,
      lastActive: s.last_updated_at,
      lastSyncTime: s.snapshot_generated_at,
      // Custom benchmark specific fields (will be undefined for platform mode)
      performance_score: s.performance_score,
      benchmarks_met: s.benchmarks_met,
      total_benchmarks: s.total_benchmarks,
      // Platform comparison fields (will be undefined for platform mode)
      platform_severity: s.platform_severity,
      platform_waste: s.platform_waste,
      severity_difference: s.severity_difference
    }));

    logger.info(`✅ Mapped ${campaignsToDisplay.length} campaigns for display`);

    setCampaigns(campaignsToDisplay);
    setFilteredCampaigns(campaignsToDisplay);
    
    // Set main currency
    if (campaignsToDisplay.length > 0) {
      const currencyCounts = campaignsToDisplay.reduce((acc, campaign) => {
        const currency = campaign.currency || 'USD';
        acc[currency] = (acc[currency] || 0) + 1;
        return acc;
      }, {});
      const mostCommonCurrency = Object.entries(currencyCounts).sort((a, b) => b[1] - a[1])[0][0];
      setMainCurrency(mostCommonCurrency);
    }
    
    // Calculate counts based on severity
    const championCount = campaignsToDisplay.filter(c => c.severity === 'CHAMPION').length;
    const healthyCount = campaignsToDisplay.filter(c => c.severity === 'HEALTHY').length;
    const criticalCount = campaignsToDisplay.filter(c => c.severity === 'CRITICAL').length;
    
    setChampionsCount(championCount);
    setHealthyCampaignsCount(healthyCount);
    setCriticalCount(criticalCount);
    
    logger.info(`📊 Severity counts - Champions: ${championCount}, Healthy: ${healthyCount}, Critical: ${criticalCount}`);
    
    // Generate insights
    const newInsights = [];
    const activeCampaigns = campaignsToDisplay.filter(c => c.actual_status === 'active');
    const pausedCampaigns = campaignsToDisplay.filter(c => c.actual_status === 'paused');
    
    if (mode === 'custom_benchmarks') {
      // Custom benchmark specific insights
      if (criticalCount > 0) {
        newInsights.push({
          severity: 'critical',
          message: `${criticalCount} campaigns are not meeting your custom performance goals.`
        });
      }
      if (championCount > 0) {
        newInsights.push({
          severity: 'success',
          message: `${championCount} campaigns are exceeding your custom benchmarks! Great work!`
        });
      }
      
      const totalWithBenchmarks = campaignsToDisplay.length;
      if (benchmarkCoverage && totalWithBenchmarks < benchmarkCoverage.total_campaigns) {
        newInsights.push({
          severity: 'info',
          message: `${benchmarkCoverage.campaigns_without_benchmarks} campaigns don't have custom benchmarks yet.`
        });
      }

      // Add insight about platform differences
      const differentSeverities = campaignsToDisplay.filter(c => c.severity_difference).length;
      if (differentSeverities > 0) {
        newInsights.push({
          severity: 'info',
          message: `${differentSeverities} campaigns have different ratings between your custom goals and platform standards.`
        });
      }
    } else {
      // Our metrics insights (existing)
      if (criticalCount > 0) {
        newInsights.push({
          severity: 'critical',
          message: `You have ${criticalCount} critical campaigns that need immediate attention.`
        });
      }
      if (championCount > 0) {
        newInsights.push({
          severity: 'success',
          message: `You have ${championCount} champion campaigns! Consider scaling their budgets.`
        });
      }
      if (pausedCampaigns.length > 0) {
        newInsights.push({
          severity: 'info',
          message: `${pausedCampaigns.length} campaigns are paused or inactive.`
        });
      }
    }
    
    const totalWaste = activeCampaigns.reduce((sum, c) => sum + (c.waste_amount || c.wasteAmount || 0), 0);
    if (totalWaste > 100) {
      newInsights.push({
        severity: 'warning',
        message: `Detected over ${formatCurrency(totalWaste, mainCurrency)} in potential budget waste across active campaigns.`
      });
    }
    
    setAiInsights(newInsights);
    setDataLoaded(true);
    setLoading(false);
    
  } catch (err) {
    logger.error('🚨 Failed to fetch campaign data:', err);
    setError(`Failed to load dashboard data: ${err.message}`);
    setCampaigns([]);
    setFilteredCampaigns([]);
    setLoading(false);
    setDataLoaded(true);
  }
}, [user, BACKEND_URL, mainCurrency, benchmarkCoverage]);

  // Handle view mode toggle
  const handleViewModeToggle = async (newMode) => {
    if (newMode === viewMode) return;
    
    if (newMode === 'custom_benchmarks' && !hasCustomBenchmarks) {
      return;
    }

    setSwitchingView(true);
    
    document.body.style.transition = 'filter 0.4s ease';
    document.body.style.filter = 'brightness(0.5)';
    
    setTimeout(async () => {
      setViewMode(newMode);
      await fetchCampaignsByMode(newMode);
      
      document.body.style.filter = 'brightness(1)';
      setTimeout(() => {
        document.body.style.transition = '';
        setSwitchingView(false);
      }, 400);
    }, 400);
  };

// ✅ FIXED: Refresh function that ensures live data
const refreshData = async () => {
  setRefreshing(true);
  setError(null);
  
  try {      
    logger.info(`🔄 Starting refresh for viewMode: ${viewMode}`);
    
    // STEP 1: Sync lifetime metrics
    logger.info('📡 Step 1: Syncing lifetime metrics...');
    const syncResponse = await fetch(`${BACKEND_URL}/api/sync-lifetime-metrics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, syncAll: false })
    });
    
    if (!syncResponse.ok) {
      throw new Error('Failed to sync lifetime metrics');
    }
    
    logger.info('✅ Step 1 Complete');
    
    // STEP 2: Generate snapshot for CURRENT view mode only
    if (viewMode === 'custom_benchmarks') {
      logger.info('🎯 Step 2: Generating CUSTOM snapshot...');
      const customResponse = await fetch(`${BACKEND_URL}/api/snapshot/custom-snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });
      
      if (!customResponse.ok) {
        throw new Error('Failed to generate custom snapshot');
      }
      logger.info('✅ Step 2 Complete - Custom Snapshot');
      
    } else {
      logger.info('📊 Step 2: Generating PLATFORM snapshot...');
      const platformResponse = await fetch(`${BACKEND_URL}/api/snapshot/update-user-snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });
      
      if (!platformResponse.ok) {
        throw new Error('Failed to generate platform snapshot');
      }
      logger.info('✅ Step 2 Complete - Platform Snapshot');
    }
    
    // STEP 3: Fetch fresh data
    logger.info('📥 Step 3: Fetching fresh data...');
    await fetchCampaignsByMode(viewMode);
    
    // STEP 4: Update benchmark coverage
    logger.info('📊 Step 4: Updating coverage...');
    await checkBenchmarkCoverage();
    
    logger.info('✅ Refresh complete!');
    
  } catch (error) {
    logger.error('❌ Refresh Error:', error);
    setError('Error refreshing: ' + error.message);
  } finally {
    setRefreshing(false);
  }
};

  // Pages management functions
  const handlePageSelect = (pageId, isSelected) => {
    if (isSelected) {
      setSelectedPages([...selectedPages, pageId]);
    } else {
      setSelectedPages(selectedPages.filter((id) => id !== pageId));
    }
  };

  const handleSelectAllPages = () => {
    const allPageIds = facebookPages.map((p) => p.id);
    const allSelected = allPageIds.every((id) => selectedPages.includes(id));
    if (allSelected) {
      setSelectedPages(selectedPages.filter((id) => !allPageIds.includes(id)));
    } else {
      const newSelected = [...selectedPages, ...allPageIds.filter((id) => !selectedPages.includes(id))];
      setSelectedPages(newSelected);
    }
  };

  const handleDeleteSelectedPages = async () => {
    if (selectedPages.length === 0) return;
    setShowRemoveConfirmModal(true);
  };

  const confirmRemovePages = async () => {
    try {
      setLoadingDelete(true);
      setShowRemoveConfirmModal(false);
      
      const response = await fetch(`${BACKEND_URL}/api/facebook/pages/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, pageIds: selectedPages }),
      });

      if (response.ok) {
        await loadFacebookPages();
        setSelectedPages([]);
      } else {
        throw new Error('Remove operation failed');
      }
    } catch (error) {
      logger.error('Error removing pages:', error);
      logger.error('❌ Failed to remove pages');
    } finally {
      setLoadingDelete(false);
    }
  };

  const checkPagesConnection = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/facebook/pages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });
        
      if (response.ok) {
        const data = await response.json();
        setFacebookPages(data.pages || []);
        
        if (data.pages && data.pages.length > 0) {
          setPagesConnected(true);
        } else {
          setPagesConnected(false);
        }
      } else {
        setPagesConnected(false);
      }
    } catch (error) {
      logger.error('Pages connection check failed:', error);
      setPagesConnected(false);
    }
  };

  const loadFacebookPages = async () => {
    try {
      setLoadingPages(true);
      const response = await fetch(`${BACKEND_URL}/api/facebook/pages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });
      
      if (response.ok) {
        const data = await response.json();
        setFacebookPages(data.pages || []);
      } else {
        throw new Error('Failed to load pages');
      }
    } catch (error) {
      logger.error('Error loading pages:', error);
      alert('Failed to load Facebook Pages. Please reconnect.');
    } finally {
      setLoadingPages(false);
    }
  };

  // UI Helper Functions
  const getSeverityColor = (severity) => {
    const colors = {
      CHAMPION: { border: 'border-2 border-green-500', background: 'bg-green-500/20' },
      HEALTHY: { border: 'border-2 border-green-400', background: 'bg-green-400/10' },
      MONITORING: { border: 'border-2 border-yellow-400', background: 'bg-yellow-400/10' },
      CRITICAL: { border: 'border-2 border-orange-500', background: 'bg-orange-500/10' },
      COMPLETED: { border: 'border-2 border-purple-500', background: 'bg-purple-500/10' },
      PAUSED: { border: 'border-2 border-gray-500', background: 'bg-gray-500/10' },
      STALE: { border: 'border-2 border-gray-600', background: 'bg-gray-600/10' },
    };
    return colors[severity] || { border: 'border-2 border-gray-500', background: 'bg-gray-500/10' };
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      CHAMPION: '🏆',
      HEALTHY: '✅',
      MONITORING: '👀',
      CRITICAL: '🚨',
      COMPLETED: '✅',
      PAUSED: '⏸️',
      STALE: '💤',
    };
    return icons[severity] || '⚠️';
  };

  const getSeverityBadgeColor = (severity) => {
    const colors = {
      CHAMPION: 'bg-green-600 text-white',
      HEALTHY: 'bg-green-500 text-white',
      MONITORING: 'bg-yellow-600 text-white',
      CRITICAL: 'bg-orange-600 text-white',
      COMPLETED: 'bg-purple-600 text-white',
      PAUSED: 'bg-gray-600 text-white',
      STALE: 'bg-gray-700 text-white',
    };
    return colors[severity] || 'bg-gray-600 text-white';
  };

  const handleViewDetails = (campaign) => {
    navigate(`/campaign-analysis/${campaign.campaign_id}`, {
      state: { 
        campaign, 
        analysisData: geminiAnalysis,
        viewMode: viewMode
      },
    });
  };

  const getStatusBarContent = () => {
    if (!showingAlert || currentStatusIndex === -1 || aiInsights.length === 0) {
      return {
        text: `User: ${user?.email} | Campaigns: ${filteredCampaigns.length} | Active: ${filteredCampaigns.filter(c => c.actual_status === 'active').length} | Page: ${currentPage}/${getTotalPages()}`,
        isAlert: false,
        clickable: false,
      };
    }

    const currentAlert = aiInsights[currentStatusIndex];
    if (!currentAlert) {
      return {
        text: `User: ${user?.email} | Campaigns: ${filteredCampaigns.length} | Active: ${filteredCampaigns.filter(c => c.actual_status === 'active').length} | Page: ${currentPage}/${getTotalPages()}`,
        isAlert: false,
        clickable: false,
      };
    }

    const alertIcon = getInsightSeverityColor(currentAlert.severity).icon;

    return {
      text: `${alertIcon} ALERT: ${currentAlert.message} ${currentAlert.campaignId ? '→ Click campaign for analysis' : ''}`,
      isAlert: true,
      clickable: !!currentAlert.campaignId,
      campaign: currentAlert.campaignId ? campaigns.find((c) => c.campaign_id === currentAlert.campaignId) : null,
    };
  };

  const handleStatusBarClick = () => {
    const statusContent = getStatusBarContent();
    if (statusContent.clickable && statusContent.campaign) {
      handleViewDetails(statusContent.campaign);
    }
  };

  const handleCampaignSelect = (campaignId, isSelected) => {
    if (isSelected) {
      setSelectedCampaigns([...selectedCampaigns, campaignId]);
    } else {
      setSelectedCampaigns(selectedCampaigns.filter((id) => id !== campaignId));
    }
  };

  const handleSelectAllCampaigns = () => {
    const allCampaignIds = filteredCampaigns.map((c) => c.campaign_id);
    const allSelected = allCampaignIds.every((id) => selectedCampaigns.includes(id));
    if (allSelected) {
      setSelectedCampaigns(selectedCampaigns.filter((id) => !allCampaignIds.includes(id)));
    } else {
      const newSelected = [...selectedCampaigns, ...allCampaignIds.filter((id) => !selectedCampaigns.includes(id))];
      setSelectedCampaigns(newSelected);
    }
  };

  const handleDeleteSelectedCampaigns = async () => {
    if (selectedCampaigns.length === 0) return;
    if (!window.confirm(`⚠️ Delete ${selectedCampaigns.length} selected campaigns?\n\nThis will permanently remove the selected campaigns and their data. This action cannot be undone.`)) {
      return;
    }

    try {
      setLoadingDelete(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(`${BACKEND_URL}/api/user/delete-campaigns`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ userId: user.id, itemIds: selectedCampaigns }),
      });

      if (response.ok) {
        setCampaigns(prev => prev.filter(c => !selectedCampaigns.includes(c.campaign_id)));
        setFilteredCampaigns(prev => prev.filter(c => !selectedCampaigns.includes(c.campaign_id)));
        
        setSelectedCampaigns([]);
        setShowManageDataModal(false);
        
        const remainingCampaigns = campaigns.filter(c => !selectedCampaigns.includes(c.campaign_id));
        const championCount = remainingCampaigns.filter(c => c.severity === 'CHAMPION').length;
        const healthyCount = remainingCampaigns.filter(c => c.severity === 'HEALTHY').length;
        const criticalCount = remainingCampaigns.filter(c => c.severity === 'CRITICAL').length;
        
        setChampionsCount(championCount);
        setHealthyCampaignsCount(healthyCount);
        setCriticalCount(criticalCount);
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (error) {
      logger.error('Error deleting campaigns:', error);
      alert('❌ Failed to delete campaigns. Please try again.');
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleOpenManageCampaigns = () => {
    setShowSettingsDropdown(false);
    setShowManageDataModal(true);
    setSelectedCampaigns([]);
  };

  const filterCampaigns = () => {
    let filtered = [...campaigns];
    if (platformFilter !== 'all') {
      filtered = filtered.filter((campaign) => campaign.platform?.toLowerCase() === platformFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((campaign) => campaign.actual_status === statusFilter);
    }
    if (severityFilter !== 'all') {
      filtered = filtered.filter((campaign) => campaign.severity === severityFilter);
    }
    if (searchFilter.trim()) {
      const searchTerm = searchFilter.toLowerCase();
      filtered = filtered.filter(
        (campaign) =>
          campaign.name?.toLowerCase().includes(searchTerm) ||
          campaign.campaign_id?.toLowerCase().includes(searchTerm) ||
          campaign.searchableId?.toLowerCase().includes(searchTerm)
      );
    }
    if (accountFilter !== 'all') {
      filtered = filtered.filter((campaign) => String(campaign.account_id).trim() === String(accountFilter).trim());
    }
    setFilteredCampaigns(filtered);
    setCurrentPage(1);
  };

  const getUniqueAccounts = () => {
    const accounts = [...new Set(campaigns.map((c) => c.account_id))];
    return accounts.filter((account) => account && account !== 'unknown');
  };

  const getFilteredAccounts = () => {
    const accounts = getUniqueAccounts();
    if (!accountSearchTerm.trim()) return accounts;
    return accounts.filter((account) => String(account).toLowerCase().includes(accountSearchTerm.toLowerCase()));
  };

  const getTotalPages = () => {
    if (campaignsPerPage >= 9999) return 1;
    return Math.ceil(filteredCampaigns.length / campaignsPerPage);
  };

  const getCurrentPageCampaigns = () => {
    if (campaignsPerPage >= 9999) return filteredCampaigns;
    const startIndex = (currentPage - 1) * campaignsPerPage;
    const endIndex = startIndex + campaignsPerPage;
    return filteredCampaigns.slice(startIndex, endIndex);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= getTotalPages()) {
      setCurrentPage(newPage);
    }
  };

  const clearAllFilters = () => {
    setPlatformFilter('all');
    setStatusFilter('all');
    setSeverityFilter('all');
    setSearchFilter('');
    setAccountFilter('all');
    setAccountSearchTerm('');
    setCurrentPage(1);
  };

  // Effects
  useEffect(() => {
    if (!authLoading && isAuthenticated && user && !dataLoaded) {
      checkBenchmarkCoverage();
      fetchCampaignsByMode(viewMode);
    } else if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, user, navigate, dataLoaded, fetchCampaignsByMode, checkBenchmarkCoverage, viewMode]);

  useEffect(() => {
    if (user?.id && dataLoaded) {
      checkPagesConnection();
    }
  }, [user?.id, dataLoaded]);

  useEffect(() => {
    filterCampaigns();
  }, [campaigns, platformFilter, statusFilter, severityFilter, searchFilter, accountFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setShowAccountDropdown(false);
      }
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (aiInsights.length === 0) return;
    const interval = setInterval(() => {
      setCurrentStatusIndex((prev) => {
        if (prev === -1) {
          setShowingAlert(true);
          return 0;
        } else if (prev < aiInsights.length - 1) {
          return prev + 1;
        } else {
          setShowingAlert(false);
          return -1;
        }
      });
    }, 6000);
    return () => clearInterval(interval);
  }, [aiInsights]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-8 relative animate-spin">
            <div className="text-4xl">🔐</div>
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text mb-2">
            Checking Authentication...
          </h2>
          <p className="text-gray-400">Verifying your access credentials</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔐</div>
          <p className="text-red-500 text-xl font-bold mb-4">Authentication Required</p>
          <p className="text-gray-400 mb-6">Please log in to access Campaign Detective</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading && !refreshing && !switchingView) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-8 relative animate-pulse">
            <div className="text-4xl">🤖</div>
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text mb-2">
            Loading Campaign Data...
          </h2>
          <p className="text-gray-400">Preparing comprehensive campaign analysis for {user.email}</p>
        </div>
      </div>
    );
  }

  if (error && !refreshing) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🤖💥</div>
          <p className="text-red-500 text-xl font-bold mb-4">{error}</p>
          <button
            onClick={refreshData}
            className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
          >
            🔄 Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen text-white transition-colors duration-500 ${
      viewMode === 'custom_benchmarks' 
        ? 'bg-gradient-to-br from-gray-50 to-gray-100' 
        : 'bg-black'
    }`}>
      <div className="p-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className={`border rounded-lg px-3 py-2 text-sm font-medium hover:bg-opacity-30 transform hover:-translate-x-0.5 transition-all ${
              viewMode === 'custom_benchmarks'
                ? 'bg-blue-100 border-blue-400 text-blue-700'
                : 'bg-green-900/20 border-green-500 text-green-500'
            }`}
          >
            ← Dashboard
          </button>
        </div>

        <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
            <div className="flex-1 text-center lg:text-left">
              <h1 className={`text-3xl md:text-4xl lg:text-5xl font-black mb-2 ${
                viewMode === 'custom_benchmarks'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text'
                  : 'bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text'
              }`}>
                CAMPAIGN OPTIMIZATION CENTER
              </h1>
              <p className={`text-base lg:text-lg ${
                viewMode === 'custom_benchmarks' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                Advanced campaign analysis and optimization for {user.email}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 justify-center lg:justify-end">
              <button
                onClick={refreshData}
                disabled={refreshing || loading}
                className={`border px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 ${
                  viewMode === 'custom_benchmarks'
                    ? 'bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100'
                    : 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20'
                }`}
              >
                {refreshing ? '🔄 Syncing...' : '🔄 Refresh Data'}
              </button>

              {pagesConnected ? (
                <button
                  onClick={() => setShowPagesModal(true)}
                  className={`border px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'custom_benchmarks'
                      ? 'bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100'
                      : 'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20'
                  }`}
                >
                  📄 View Pages ({facebookPages.length})
                </button>
              ) : (
                <button
                  onClick={() => navigate('/integrations?highlight=facebook-pages')}
                  className={`border px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'custom_benchmarks'
                      ? 'bg-orange-50 border-orange-300 text-orange-600 hover:bg-orange-100'
                      : 'bg-orange-500/10 border-orange-500/30 text-orange-400 hover:bg-orange-500/20'
                  }`}
                >
                  📄 Connect Pages
                </button>
              )}

              <div className="relative" ref={settingsDropdownRef}>
                <button
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  className={`border px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'custom_benchmarks'
                      ? 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-800/80 border-gray-600 text-white hover:bg-green-900/10 hover:border-green-500'
                  }`}
                >
                  ⚙️ Settings
                </button>
                {showSettingsDropdown && (
                  <div className={`absolute top-full right-0 rounded-lg mt-2 min-w-[200px] z-50 shadow-xl ${
                    viewMode === 'custom_benchmarks'
                      ? 'bg-white border border-gray-200'
                      : 'bg-gray-800 border border-gray-600'
                  }`}>
                    <button
                      onClick={handleOpenManageCampaigns}
                      className={`w-full text-left px-4 py-3 text-sm ${
                        viewMode === 'custom_benchmarks'
                          ? 'text-gray-700 hover:bg-gray-50 border-b border-gray-200'
                          : 'text-gray-300 hover:bg-gray-700 border-b border-gray-700'
                      }`}
                    >
                      🗑️ Delete Campaigns
                    </button>
                    <button
                      onClick={() => setShowSettingsDropdown(false)}
                      className={`w-full text-left px-4 py-3 text-sm ${
                        viewMode === 'custom_benchmarks'
                          ? 'text-gray-700 hover:bg-gray-50'
                          : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modals - keeping existing modal code */}
        {showRemoveConfirmModal && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[1100] p-4">
            <div className="bg-gray-800 rounded-lg border border-red-500/30 p-6 max-w-md w-full">
              <h3 className="text-red-400 text-lg font-bold mb-4">Remove Pages</h3>
              <p className="text-gray-300 mb-6">
                Ready to clean house? These pages will be disconnected (you can always reconnect later)! 🏠
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRemoveConfirmModal(false)}
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemovePages}
                  disabled={loadingDelete}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-500 disabled:opacity-50"
                >
                  {loadingDelete ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showManageDataModal && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-4"
            onClick={() => setShowManageDataModal(false)}
          >
            <div
              className="bg-gray-800 rounded-xl border border-gray-600 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-6 border-b border-gray-700">
                <h2 className="text-red-500 text-2xl font-bold">🗑️ Delete Campaigns</h2>
                <button
                  onClick={() => setShowManageDataModal(false)}
                  className="text-gray-400 hover:text-white text-xl p-2"
                >
                  ❌
                </button>
              </div>
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-gray-700 flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <h3 className="text-white text-lg font-bold">Select Campaigns to Delete ({filteredCampaigns.length})</h3>
                    {selectedCampaigns.length > 0 && (
                      <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        {selectedCampaigns.length} selected
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {filteredCampaigns.length > 0 && (
                      <button
                        onClick={handleSelectAllCampaigns}
                        className="bg-blue-500/10 border border-blue-500/30 text-blue-400 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-500/20"
                      >
                        {filteredCampaigns.every((c) => selectedCampaigns.includes(c.campaign_id))
                          ? '☑️ Deselect All'
                          : '☐ Select All'}
                      </button>
                    )}
                    {selectedCampaigns.length > 0 && (
                      <button
                        onClick={handleDeleteSelectedCampaigns}
                        disabled={loadingDelete}
                        className={`bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-500/20 ${
                          loadingDelete ? 'opacity-50' : ''
                        }`}
                      >
                        {loadingDelete ? '🔄 Deleting...' : `🗑️ Delete Selected (${selectedCampaigns.length})`}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-auto p-4">
                  {filteredCampaigns.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400">No campaigns found</p>
                    </div>
                  ) : (
                    filteredCampaigns.map((campaign) => {
                      const isSelected = selectedCampaigns.includes(campaign.campaign_id);
                      return (
                        <div
                          key={campaign.campaign_id}
                          className="flex items-center gap-4 p-4 bg-gray-900/50 rounded-lg mb-2 border border-gray-700"
                        >
                          <div className="flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleCampaignSelect(campaign.campaign_id, e.target.checked)}
                              className="w-5 h-5 cursor-pointer"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-bold break-words">{campaign.name}</div>
                            <div className="flex gap-4 text-xs text-gray-400 flex-wrap">
                              <span>Campaign ID: {campaign.campaign_id}</span>
                              <span>Status: {campaign.actual_status}</span>
                              <span>Spend: {formatCurrency(campaign.spend, campaign.currency)}</span>
                              <span>Platform: {campaign.platform}</span>
                            </div>
                          </div>
                          {(campaign.wasteAmount > 0 || campaign.waste_amount > 0) && (
                            <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-2 py-1 rounded-full text-xs font-bold flex-shrink-0">
                              💸 {formatCurrency(campaign.wasteAmount || campaign.waste_amount, campaign.currency)} waste
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showPagesModal && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-4"
            onClick={() => setShowPagesModal(false)}
          >
            <div
              className="bg-gray-800 rounded-xl border border-gray-600 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-6 border-b border-gray-700">
                <div>
                  <h2 className="text-blue-400 text-2xl font-bold">Facebook Pages</h2>
                  <p className="text-gray-400 text-sm mt-1">Manage your connected pages ({facebookPages.length})</p>
                </div>
                <button
                  onClick={() => setShowPagesModal(false)}
                  className="text-gray-400 hover:text-white text-xl p-2"
                >
                  ❌
                </button>
              </div>
              
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-700">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate('/integrations?highlight=facebook-pages')}
                        className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-2 rounded-lg text-sm hover:bg-green-500/30 transition-colors"
                      >
                        🔗 Connect More Pages
                      </button>
                      <button
                        onClick={loadFacebookPages}
                        disabled={loadingPages}
                        className="bg-blue-500/20 border border-blue-500/50 text-blue-400 px-4 py-2 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                      >
                        {loadingPages ? '🔄 Loading...' : '🔄 Refresh'}
                      </button>
                    </div>
                    
                    {facebookPages.length > 0 && (
                      <div className="flex gap-2">
                        <button
                          onClick={handleSelectAllPages}
                          className="bg-blue-500/10 border border-blue-500/30 text-blue-400 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-500/20"
                        >
                          {facebookPages.every((p) => selectedPages.includes(p.id))
                            ? '☑️ Deselect All'
                            : '☐ Select All'}
                        </button>
                        {selectedPages.length > 0 && (
                          <button
                            onClick={handleDeleteSelectedPages}
                            disabled={loadingDelete}
                            className={`bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-500/20 ${
                              loadingDelete ? 'opacity-50' : ''
                            }`}
                          >
                            {loadingDelete ? '🔄 Removing...' : `🗑️ Remove Selected (${selectedPages.length})`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex-1 overflow-auto p-6">
                  {loadingPages ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">📄</div>
                      <p className="text-gray-400">Loading your Facebook Pages...</p>
                    </div>
                  ) : facebookPages.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">📄</div>
                      <p className="text-gray-400 mb-4">No Facebook Pages connected</p>
                      <button
                        onClick={() => navigate('/integrations?highlight=facebook-pages')}
                        className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-2 rounded-lg text-sm hover:bg-green-500/30"
                      >
                        Connect Facebook Pages
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {facebookPages.map((page) => {
                        const isSelected = selectedPages.includes(page.id);
                        return (
                          <div
                            key={page.id}
                            className="flex items-center gap-4 p-4 bg-gray-900/50 rounded-lg border border-gray-700"
                          >
                            <div className="flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handlePageSelect(page.id, e.target.checked)}
                                className="w-5 h-5 cursor-pointer"
                              />
                            </div>
                            {page.picture?.data?.url && (
                              <img
                                src={page.picture.data.url}
                                alt={page.name}
                                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-white font-bold break-words">{page.name}</h4>
                              <p className="text-gray-400 text-sm">Page ID: {page.id}</p>
                            </div>
                            <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-3 py-1 rounded-full text-xs font-bold flex-shrink-0">
                              ✅ Connected
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <div className="p-6 border-t border-gray-700 bg-gray-900/50">
                  <p className="text-xs text-gray-500 text-center">
                    Marketing genius lies in understanding your audience. Every connection brings you closer to that understanding.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Analysis Status */}
        {(aiAnalyzing || refreshing) && (
          <div className={`border rounded-lg p-4 mb-8 flex items-center gap-2 ${
            viewMode === 'custom_benchmarks'
              ? 'bg-blue-50 border-blue-300 text-blue-600'
              : 'bg-green-500/10 border-green-500/30 text-green-500'
          }`}>
            <span className="text-xl animate-pulse">🤖</span>
            <span>{refreshing ? 'Syncing and refreshing your campaign data...' : 'Performing enhanced AI analysis on your campaigns...'}</span>
          </div>
        )}

        {/* Status Bar */}
        <div
          className={`rounded-lg p-3 mb-8 border text-center transition-all ${
            showingAlert 
              ? viewMode === 'custom_benchmarks'
                ? 'bg-blue-50 border-blue-300'
                : 'bg-gradient-to-r from-gray-900/90 to-gray-800/80 border-green-500/30'
              : viewMode === 'custom_benchmarks'
              ? 'bg-white border-gray-200'
              : 'bg-gray-900/80 border-gray-700'
          } ${getStatusBarContent().clickable ? 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5' : ''}`}
          onClick={handleStatusBarClick}
        >
          <p className={`text-sm m-0 transition-colors ${
            showingAlert 
              ? viewMode === 'custom_benchmarks' ? 'text-blue-700 font-medium' : 'text-green-500 font-medium'
              : viewMode === 'custom_benchmarks' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            {getStatusBarContent().text}
          </p>
        </div>

        {/* ENHANCED TOGGLE SECTION */}
        <div className={`rounded-xl p-6 mb-8 border relative overflow-hidden ${
          viewMode === 'custom_benchmarks'
            ? 'bg-gradient-to-br from-blue-50 to-purple-50 border-purple-300 shadow-xl'
            : 'bg-gradient-to-br from-gray-900 to-gray-800 border-green-500/30'
        }`}>
          <div className="absolute inset-0 opacity-5">
            <div className={`h-full w-full ${
              viewMode === 'custom_benchmarks' 
                ? 'bg-gradient-to-br from-blue-600 to-purple-600' 
                : 'bg-gradient-to-br from-green-600 to-blue-600'
            }`} />
          </div>
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleViewModeToggle('our_metrics')}
                  disabled={switchingView}
                  className={`px-6 py-3 rounded-lg font-bold text-sm transition-all transform hover:-translate-y-0.5 ${
                    viewMode === 'our_metrics'
                      ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white shadow-lg scale-105'
                      : viewMode === 'custom_benchmarks'
                      ? 'bg-white/70 text-gray-600 hover:bg-white/90'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  } ${switchingView ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className="flex items-center gap-2">
                    📊 Platform Snapshot
                    {viewMode === 'our_metrics' && <span className="text-xs">✓ Active</span>}
                  </span>
                </button>

                <div className={`text-2xl animate-pulse ${
                  viewMode === 'custom_benchmarks' ? 'text-purple-400' : 'text-gray-600'
                }`}>
                  ⟷
                </div>

                <button
                  onClick={() => handleViewModeToggle('custom_benchmarks')}
                  disabled={!hasCustomBenchmarks || switchingView}
                  className={`px-6 py-3 rounded-lg font-bold text-sm transition-all transform hover:-translate-y-0.5 relative ${
                    viewMode === 'custom_benchmarks'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                      : hasCustomBenchmarks
                      ? 'bg-white/70 text-gray-600 hover:bg-white/90'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  } ${switchingView ? 'opacity-50' : ''}`}
                >
                  <span className="flex items-center gap-2">
                    🎯 Custom Snapshot
                    {viewMode === 'custom_benchmarks' && <span className="text-xs">✓ Active</span>}
                  </span>
                  {!hasCustomBenchmarks && (
                    <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full animate-bounce">
                      Set Goals First
                    </span>
                  )}
                </button>
              </div>
              
              {hasCustomBenchmarks && benchmarkCoverage && (
                <div className={`text-xs ${
                  viewMode === 'custom_benchmarks' ? 'text-purple-700' : 'text-gray-500'
                }`}>
                  <span className="font-semibold">{benchmarkCoverage.campaigns_with_custom_benchmarks}</span> campaigns with custom goals
                </div>
              )}
            </div>

            {viewMode === 'custom_benchmarks' && hasCustomBenchmarks && (
              <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 mt-4">
                <p className="text-sm text-purple-700">
                  🎯 <strong>Custom Mode:</strong> Campaigns are evaluated against YOUR specific performance goals.
                  Only showing campaigns with custom benchmarks set.
                </p>
              </div>
            )}
            
            {viewMode === 'our_metrics' && (
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 mt-4">
                <p className="text-sm text-green-400">
                  📊 <strong>Platform Mode:</strong> Campaigns are evaluated against industry standards and platform best practices.
                </p>
              </div>
            )}
            
            {!hasCustomBenchmarks && (
              <div className={`border rounded-lg p-3 mt-4 ${
                viewMode === 'custom_benchmarks'
                  ? 'bg-orange-50 border-orange-200'
                  : 'bg-orange-900/10 border-orange-500/30'
              }`}>
                <p className={`text-sm ${
                  viewMode === 'custom_benchmarks' ? 'text-orange-700' : 'text-orange-400'
                }`}>
                  💡 <strong>Get Started:</strong> Set custom benchmarks to unlock personalized analysis.{' '}
                  <button
                    onClick={() => navigate('/settings/performance-goals')}
                    className="underline font-semibold hover:no-underline"
                  >
                    Set your first custom goals →
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className={`p-4 lg:p-6 rounded-xl border text-center ${
            viewMode === 'custom_benchmarks'
              ? 'bg-gradient-to-br from-white to-purple-50 border-purple-200 shadow-md'
              : 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700'
          }`}>
            <div className={`text-2xl md:text-3xl font-bold mb-2 ${
              viewMode === 'custom_benchmarks' ? 'text-purple-900' : 'text-white'
            }`}>
              {filteredCampaigns.length}
            </div>
            <div className={`text-sm ${
              viewMode === 'custom_benchmarks' ? 'text-purple-600' : 'text-gray-400'
            }`}>
              {viewMode === 'custom_benchmarks' ? 'With Custom Goals' : 'Total Campaigns'}
            </div>
          </div>
          
          <div className={`p-4 lg:p-6 rounded-xl border text-center ${
            viewMode === 'custom_benchmarks'
              ? 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-300 shadow-md'
              : 'bg-gradient-to-br from-green-900 to-green-800 border-green-600'
          }`}>
            <div className={`text-2xl md:text-3xl font-bold mb-2 ${
              viewMode === 'custom_benchmarks' ? 'text-green-800' : 'text-green-400'
            }`}>
              {championsCount}
            </div>
            <div className={`text-sm ${
              viewMode === 'custom_benchmarks' ? 'text-green-700' : 'text-green-300'
            }`}>
              {viewMode === 'custom_benchmarks' ? 'Exceeding Goals' : 'Champions'}
            </div>
          </div>
          
          <div className={`p-4 lg:p-6 rounded-xl border text-center ${
            viewMode === 'custom_benchmarks'
              ? 'bg-gradient-to-br from-red-50 to-orange-100 border-red-300 shadow-md'
              : 'bg-gradient-to-br from-orange-900 to-orange-800 border-orange-600'
          }`}>
            <div className={`text-2xl md:text-3xl font-bold mb-2 ${
              viewMode === 'custom_benchmarks' ? 'text-red-800' : 'text-orange-500'
            }`}>
              {criticalCount}
            </div>
            <div className={`text-sm ${
              viewMode === 'custom_benchmarks' ? 'text-red-700' : 'text-orange-300'
            }`}>
              {viewMode === 'custom_benchmarks' ? 'Missing Goals' : 'Critical'}
            </div>
          </div>
          
          <div className={`p-4 lg:p-6 rounded-xl border text-center ${
            viewMode === 'custom_benchmarks'
              ? 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300 shadow-md'
              : 'bg-gradient-to-br from-blue-900 to-blue-800 border-blue-600'
          }`}>
            <div className={`text-2xl md:text-3xl font-bold mb-2 ${
              viewMode === 'custom_benchmarks' ? 'text-blue-800' : 'text-blue-400'
            }`}>
              {healthyCampaignsCount}
            </div>
            <div className={`text-sm ${
              viewMode === 'custom_benchmarks' ? 'text-blue-700' : 'text-blue-300'
            }`}>
              {viewMode === 'custom_benchmarks' ? 'Meeting Goals' : 'Healthy'}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`rounded-xl p-4 lg:p-6 mb-8 border ${
          viewMode === 'custom_benchmarks'
            ? 'bg-white border-gray-200 shadow-sm'
            : 'bg-gray-900/80 border-gray-700'
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <h3 className={`text-xl font-bold ${
              viewMode === 'custom_benchmarks' ? 'text-gray-900' : 'text-green-500'
            }`}>
              🔍 Filter & Search
            </h3>
            <button
              onClick={clearAllFilters}
              className={`border px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'custom_benchmarks'
                  ? 'bg-red-50 border-red-300 text-red-600 hover:bg-red-100'
                  : 'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20'
              }`}
            >
              🗑️ Clear All
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="flex flex-col gap-2">
              <label className={`text-sm font-medium ${
                viewMode === 'custom_benchmarks' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                Search Campaigns
              </label>
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Search by name or ID..."
                className={`border rounded-lg px-3 py-2 text-sm outline-none transition-all ${
                  viewMode === 'custom_benchmarks'
                    ? 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                    : 'bg-gray-800/80 border-gray-600 text-white focus:border-green-500 focus:shadow-[0_0_0_2px_rgba(16,185,129,0.2)]'
                }`}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className={`text-sm font-medium ${
                viewMode === 'custom_benchmarks' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                Platform
              </label>
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className={`border rounded-lg px-3 py-2 text-sm outline-none cursor-pointer transition-all ${
                  viewMode === 'custom_benchmarks'
                    ? 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                    : 'bg-gray-800/80 border-gray-600 text-white focus:border-green-500 focus:shadow-[0_0_0_2px_rgba(16,185,129,0.2)]'
                }`}
              >
                <option value="all">All Platforms</option>
                <option value="facebook">Facebook</option>
                <option value="google">Google</option>
                <option value="tiktok">TikTok</option>
                <option value="linkedin">LinkedIn</option>
                <option value="twitter">Twitter</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className={`text-sm font-medium ${
                viewMode === 'custom_benchmarks' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`border rounded-lg px-3 py-2 text-sm outline-none cursor-pointer transition-all ${
                  viewMode === 'custom_benchmarks'
                    ? 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                    : 'bg-gray-800/80 border-gray-600 text-white focus:border-green-500 focus:shadow-[0_0_0_2px_rgba(16,185,129,0.2)]'
                }`}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="stale">Stale</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className={`text-sm font-medium ${
                viewMode === 'custom_benchmarks' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                Performance
              </label>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className={`border rounded-lg px-3 py-2 text-sm outline-none cursor-pointer transition-all ${
                  viewMode === 'custom_benchmarks'
                    ? 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                    : 'bg-gray-800/80 border-gray-600 text-white focus:border-green-500 focus:shadow-[0_0_0_2px_rgba(16,185,129,0.2)]'
                }`}
              >
                <option value="all">All Performance</option>
                <option value="CHAMPION">Champion</option>
                <option value="HEALTHY">Healthy</option>
                <option value="MONITORING">Monitoring</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>

            <div className="flex flex-col gap-2" ref={accountDropdownRef}>
              <label className={`text-sm font-medium ${
                viewMode === 'custom_benchmarks' ? 'text-gray-700' : 'text-gray-300'
              }`}>
                Account
              </label>
              <div className="relative">
                <button
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                  className={`w-full border rounded-lg px-3 py-2 text-sm outline-none cursor-pointer flex justify-between items-center transition-all ${
                    viewMode === 'custom_benchmarks'
                      ? 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                      : 'bg-gray-800/80 border-gray-600 text-white focus:border-green-500 focus:shadow-[0_0_0_2px_rgba(16,185,129,0.2)]'
                  }`}
                >
                  {accountFilter === 'all' ? 'All Accounts' : accountFilter}
                  <span className={`text-xs ${viewMode === 'custom_benchmarks' ? 'text-gray-600' : 'text-gray-400'}`}>
                    {showAccountDropdown ? '▲' : '▼'}
                  </span>
                </button>
                {showAccountDropdown && (
                  <div className={`absolute top-full left-0 right-0 rounded-lg mt-1 z-50 max-h-52 overflow-hidden ${
                    viewMode === 'custom_benchmarks'
                      ? 'bg-white border border-gray-300'
                      : 'bg-gray-800 border border-gray-600'
                  }`}>
                    <input
                      type="text"
                      value={accountSearchTerm}
                      onChange={(e) => setAccountSearchTerm(e.target.value)}
                      placeholder="Search accounts..."
                      className={`w-full border-0 border-b px-3 py-2 text-sm outline-none ${
                        viewMode === 'custom_benchmarks'
                          ? 'bg-gray-50 border-gray-300 text-gray-900'
                          : 'bg-gray-700/80 border-gray-600 text-white'
                      }`}
                    />
                    <div className="max-h-40 overflow-y-auto">
                      <button
                        onClick={() => {
                          setAccountFilter('all');
                          setShowAccountDropdown(false);
                          setAccountSearchTerm('');
                        }}
                        className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-all ${
                          accountFilter === 'all'
                            ? viewMode === 'custom_benchmarks'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-green-500/20 text-green-500'
                            : viewMode === 'custom_benchmarks'
                            ? 'text-gray-900 hover:bg-gray-100'
                            : 'text-white hover:bg-gray-700/50'
                        }`}
                      >
                        All Accounts
                      </button>
                      {getFilteredAccounts().map((account) => (
                        <button
                          key={account}
                          onClick={() => {
                            setAccountFilter(account);
                            setShowAccountDropdown(false);
                            setAccountSearchTerm('');
                          }}
                          className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-all ${
                            accountFilter === account
                              ? viewMode === 'custom_benchmarks'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-500/20 text-green-500'
                              : viewMode === 'custom_benchmarks'
                              ? 'text-gray-900 hover:bg-gray-100'
                              : 'text-white hover:bg-gray-700/50'
                          }`}
                        >
                          {account}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Link to Performance Goals */}
        {viewMode === 'our_metrics' && (
          <div className="text-center mb-8">
            <p className="text-gray-400 text-sm">
              {hasCustomBenchmarks 
                ? "Want to adjust your custom goals or set them for more campaigns?"
                : "Is our analysis not quite right?"
              }
              <a href="/settings/performance-goals" className="text-green-400 hover:underline ml-1 font-semibold">
                {hasCustomBenchmarks 
                  ? "Manage your custom performance goals →"
                  : "Set your custom performance goals for a more tailored analysis →"}
              </a>
            </p>
          </div>
        )}

        {/* Campaigns Grid */}
        {filteredCampaigns.length === 0 ? (
          <div className={`text-center py-12 rounded-2xl border ${
            viewMode === 'custom_benchmarks'
              ? 'bg-white border-gray-200'
              : 'bg-gray-900/50 border-gray-700'
          }`}>
            <div className="text-6xl mb-4">
              {viewMode === 'custom_benchmarks' ? '🎯' : '🤖'}
            </div>
            <p className={`text-2xl font-bold mb-2 ${
              viewMode === 'custom_benchmarks' ? 'text-blue-600' : 'text-green-500'
            }`}>
              {viewMode === 'custom_benchmarks' ? 'No Custom Benchmarks Set' : 'No Campaign Data Found'}
            </p>
            <p className={`mb-6 ${
              viewMode === 'custom_benchmarks' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              {viewMode === 'custom_benchmarks'
                ? 'Set custom benchmarks in Performance Goals to see campaigns here.'
                : campaigns.length === 0
                ? 'No campaigns found. Connect your ad accounts to start analyzing.'
                : 'No campaigns match your current filters.'}
            </p>
            {viewMode === 'custom_benchmarks' ? (
              <button
                onClick={() => navigate('/settings/performance-goals')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
              >
                Set Custom Benchmarks
              </button>
            ) : campaigns.length === 0 ? (
              <button
                onClick={() => navigate('/integrations')}
                className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
              >
                Connect Ad Accounts
              </button>
            ) : (
              <button
                onClick={clearAllFilters}
                className={`border px-6 py-3 rounded-lg font-bold transform hover:-translate-y-0.5 transition-all ${
                  viewMode === 'custom_benchmarks'
                    ? 'bg-red-50 border-red-300 text-red-600 hover:bg-red-100'
                    : 'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20'
                }`}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-8">
              {getCurrentPageCampaigns().map((campaign) => {
                const severityColor = getSeverityColor(campaign.severity);
                const severityIcon = getSeverityIcon(campaign.severity);
                const badgeColor = getSeverityBadgeColor(campaign.severity);
                const wasteAmount = campaign.wasteAmount || campaign.waste_amount || 0;
                const isActive = campaign.actual_status === 'active';
                
                return (
                  <div
                    key={campaign.campaign_id}
                    className={`rounded-2xl p-4 lg:p-6 border transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                      viewMode === 'custom_benchmarks'
                        ? `bg-white ${severityColor.border} shadow-sm`
                        : `bg-gray-900/50 ${severityColor.border} ${severityColor.background}`
                    }`}
                  >
                    {/* FIXED: Removed duplicate platform badge from top */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{severityIcon}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${badgeColor}`}>
                          {campaign.severity}
                        </span>
                      </div>

                      <div className={`text-xs text-right ${
                        viewMode === 'custom_benchmarks' ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {(() => {
                          const dateToUse = campaign.lastSyncTime || campaign.lastActive || campaign.last_updated_at || campaign.recorded_at;
                          if (!dateToUse) return 'No date';
                          const date = new Date(dateToUse);
                          if (isNaN(date.getTime())) return 'Invalid date';
                          return date.toLocaleDateString() + ', ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        })()}
                      </div>
                    </div>

                    <h3 className={`font-bold text-base lg:text-lg mb-2 truncate ${
                      viewMode === 'custom_benchmarks' ? 'text-gray-900' : 'text-white'
                    }`}>
                      {campaign.name}
                    </h3>

                    <div className={`text-sm mb-4 space-y-1 ${
                      viewMode === 'custom_benchmarks' ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      <p className="truncate">ID: {campaign.campaign_id}</p>
                      <p>Platform: {campaign.platform?.charAt(0).toUpperCase() + campaign.platform?.slice(1)}</p>
                      <p>Objective: {getObjectiveDisplayName(campaign.campaign_objective)}</p>
                      <p>
                        Status: <span className={`font-semibold ${
                          campaign.actual_status === 'active' ? 'text-green-500' : 
                          campaign.actual_status === 'paused' ? 'text-yellow-500' : 
                          campaign.actual_status === 'completed' ? 'text-purple-500' :
                          campaign.actual_status === 'stale' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {campaign.actual_status?.charAt(0).toUpperCase() + campaign.actual_status?.slice(1)}
                        </span>
                      </p>
                    </div>

                    {/* Custom Benchmark Score Display - CLEAN UI */}
                    {viewMode === 'custom_benchmarks' && (
                      <div className="mb-4">
                        {/* Your Custom Performance */}
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-300 rounded-lg p-3 mb-2">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-semibold text-blue-700">🎯 Your Custom Goals</span>
                            <span className="text-lg font-bold text-blue-900">
                              {campaign.performance_score !== undefined && campaign.performance_score !== null
                                ? `${campaign.performance_score}%`
                                : 'N/A'}
                            </span>
                          </div>
                          <div className="w-full bg-blue-100 rounded-full h-2 mb-2">
                            <div
                              className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all"
                              style={{ width: `${campaign.performance_score || 0}%` }}
                            />
                          </div>
                          <p className="text-xs text-blue-700">
                            {campaign.benchmarks_met !== undefined && campaign.total_benchmarks !== undefined
                              ? `Meeting ${campaign.benchmarks_met}/${campaign.total_benchmarks} custom goals`
                              : 'Custom benchmarks set'}
                          </p>
                        </div>

                        {/* Platform Comparison - Only show if different */}
                        {campaign.platform_severity && campaign.severity_difference && (
                          <div className="bg-gray-100 border border-gray-300 rounded-lg p-2 flex items-center justify-between">
                            <span className="text-xs text-gray-600">📊 Platform Rating:</span>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                              campaign.platform_severity === 'CHAMPION' ? 'bg-green-600 text-white' :
                              campaign.platform_severity === 'HEALTHY' ? 'bg-green-500 text-white' :
                              campaign.platform_severity === 'MONITORING' ? 'bg-yellow-600 text-white' :
                              'bg-orange-600 text-white'
                            }`}>
                              {campaign.platform_severity}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <p className={`text-xs ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          Spend
                        </p>
                        <p className={`font-bold ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-900' : 'text-white'
                        }`}>
                          {formatCurrency(campaign.spend, campaign.currency)}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          CTR
                        </p>
                        <p className={`font-bold ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-900' : 'text-white'
                        }`}>
                          {campaign.ctr?.toFixed(2) || '0.00'}%
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          CPC
                        </p>
                        <p className={`font-bold ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-900' : 'text-white'
                        }`}>
                          {formatCurrency(campaign.cpc, campaign.currency)}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          Conversions
                        </p>
                        <p className={`font-bold ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-900' : 'text-white'
                        }`}>
                          {campaign.conversions || 0}
                        </p>
                      </div>
                      <div>
                        <p className={`text-xs ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          Conversion Rate
                        </p>
                        <p className={`font-bold ${
                          viewMode === 'custom_benchmarks' ? 'text-gray-900' : 'text-white'
                        }`}>
                          {campaign.conversion_rate?.toFixed(2) || '0.00'}%
                        </p>
                      </div>
                      {wasteAmount > 0 && isActive && (
                        <div>
                          <p className={`text-xs ${
                            viewMode === 'custom_benchmarks' ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            Est. Waste
                          </p>
                          <p className="text-red-500 font-bold">
                            {formatCurrency(wasteAmount, campaign.currency)}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewDetails(campaign)}
                        className={`w-full px-4 py-2 rounded-lg text-sm font-medium transform hover:-translate-y-0.5 transition-all ${
                          viewMode === 'custom_benchmarks'
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
                            : 'bg-gradient-to-r from-green-600 to-blue-600 text-white hover:shadow-lg'
                        }`}
                      >
                        Deep Analysis
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="flex flex-col lg:flex-row justify-between items-center mt-8 gap-4">
              <div className="flex items-center gap-2">
                <span className={`text-sm ${
                  viewMode === 'custom_benchmarks' ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  Campaigns per page:
                </span>
                <select
                  value={campaignsPerPage}
                  onChange={(e) => {
                    setCampaignsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`border rounded-lg px-3 py-1 text-sm outline-none cursor-pointer ${
                    viewMode === 'custom_benchmarks'
                      ? 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'
                      : 'bg-gray-800/80 border-gray-600 text-white focus:border-green-500'
                  }`}
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                  <option value={9999}>All</option>
                </select>
              </div>
              {campaignsPerPage < 9999 ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                      currentPage === 1
                        ? 'opacity-50 cursor-not-allowed'
                        : viewMode === 'custom_benchmarks'
                        ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        : 'bg-gray-800/80 text-white border-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    Previous
                  </button>
                  <span className={`text-sm ${
                    viewMode === 'custom_benchmarks' ? 'text-gray-600' : 'text-gray-300'
                  }`}>
                    Page {currentPage} of {getTotalPages()}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === getTotalPages()}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                      currentPage === getTotalPages()
                        ? 'opacity-50 cursor-not-allowed'
                        : viewMode === 'custom_benchmarks'
                        ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        : 'bg-gray-800/80 text-white border-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    Next
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${
                    viewMode === 'custom_benchmarks' ? 'text-gray-600' : 'text-gray-300'
                  }`}>
                    Showing all {filteredCampaigns.length} campaigns
                  </span>
                </div>
              )}
            </div>

            {/* AI Insights */}
            {aiInsights.length > 0 && (
              <div className={`mt-8 rounded-xl p-4 lg:p-6 border ${
                viewMode === 'custom_benchmarks'
                  ? 'bg-white border-gray-200'
                  : 'bg-gray-900/80 border-gray-700'
              }`}>
                <h3 className={`text-xl font-bold mb-4 ${
                  viewMode === 'custom_benchmarks' ? 'text-gray-900' : 'text-green-500'
                }`}>
                  📊 Campaign Insights & Alerts
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {aiInsights.slice(0, 6).map((insight, index) => {
                    const severityColor = getInsightSeverityColor(insight.severity);
                    return (
                      <div
                        key={index}
                        className={`p-4 rounded-lg ${
                          viewMode === 'custom_benchmarks'
                            ? insight.severity === 'critical' ? 'bg-red-50 border border-red-200'
                            : insight.severity === 'success' ? 'bg-green-50 border border-green-200'
                            : insight.severity === 'warning' ? 'bg-orange-50 border border-orange-200'
                            : 'bg-blue-50 border border-blue-200'
                            : `${severityColor.background} ${severityColor.border}`
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xl">{severityColor.icon}</span>
                          {insight.title && (
                            <h4 className={`font-bold ${
                              viewMode === 'custom_benchmarks'
                                ? insight.severity === 'critical' ? 'text-red-700'
                                : insight.severity === 'success' ? 'text-green-700'
                                : insight.severity === 'warning' ? 'text-orange-700'
                                : 'text-blue-700'
                                : 'text-white'
                            }`}>
                              {insight.title}
                            </h4>
                          )}
                        </div>
                        <p className={`text-sm mb-2 ${
                          viewMode === 'custom_benchmarks'
                            ? insight.severity === 'critical' ? 'text-red-700'
                            : insight.severity === 'success' ? 'text-green-700'
                            : insight.severity === 'warning' ? 'text-orange-700'
                            : 'text-blue-700'
                            : 'text-gray-300'
                        }`}>
                          {insight.message}
                        </p>
                        {insight.action && (
                          <p className={`text-sm font-medium ${
                            viewMode === 'custom_benchmarks' ? 'text-blue-600' : 'text-green-400'
                          }`}>
                            {insight.action}
                          </p>
                        )}
                        {insight.campaignId && (
                          <button
                            onClick={() =>
                              handleViewDetails(campaigns.find((c) => c.campaign_id === insight.campaignId))
                            }
                            className={`mt-2 border px-3 py-1 rounded-lg text-sm transition-all ${
                              viewMode === 'custom_benchmarks'
                                ? 'bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100'
                                : 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20'
                            }`}
                          >
                            View Campaign
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className={`border-t p-4 mt-8 ${
        viewMode === 'custom_benchmarks'
          ? 'bg-white border-gray-200'
          : 'bg-gray-900/80 border-gray-700'
      }`}>
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-4">
          <p className={`text-sm text-center lg:text-left ${
            viewMode === 'custom_benchmarks' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            Powered by WhyDidItDie | {filteredCampaigns.length} campaigns analyzed | {championsCount} champions | {healthyCampaignsCount} healthy | {criticalCount} critical
            {viewMode === 'custom_benchmarks' && ' | Using your custom benchmarks'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/integrations')}
              className={`border px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'custom_benchmarks'
                  ? 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                  : 'bg-gray-800/80 border-gray-600 text-gray-300 hover:bg-green-900/10 hover:border-green-500'
              }`}
            >
              🔗 Connect More Accounts
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetective;